/**
 * SecondFacteurPage — double authentification TOTP (port de Pimer, 2026-09-16).
 *
 * Deux écrans, choisis par le SERVEUR (/api/auth/me → mfa) et jamais par le
 * navigateur seul :
 *   - « enrolement » : un super_admin sans facteur en pose un (QR code ou clé,
 *     puis un premier code) ;
 *   - « defi »       : un compte qui porte un TOTP vérifié saisit son code.
 *
 * L'enrôlement et la vérification passent par Supabase Auth (GoTrue), jamais par
 * le serveur ServUp. Un code juste fait passer la session en aal2 ; la garde du
 * serveur (backend/utils/secondFacteur.js) laisse alors passer les routes.
 *
 * Durci comme chez Pimer : garde anti-double-soumission, et re-contrôle du niveau
 * AAL avant d'afficher « code incorrect » (un verify abouti dont la réponse s'est
 * perdue laisse la session en aal2 : on continue au lieu d'afficher une fausse erreur).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useViewport } from '../hooks/useViewport';
import BlocCleTotp, { ETAPES_ENROLEMENT } from '../components/securite/BlocCleTotp';
import ChampCodeTotp from '../components/securite/ChampCodeTotp';
import { EnrolementTotp, preparerEnrolementTotp, verifierCodeTotp } from '../components/securite/totp';

interface SecondFacteurPageProps {
  mode: 'defi' | 'enrolement';
  facteurId?: string;
  email: string;
  onValide: () => void;
  onLogout: () => void;
}

function SecondFacteurPage({ mode, facteurId, email, onValide, onLogout }: SecondFacteurPageProps) {
  const { isPhone } = useViewport();
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [enrolement, setEnrolement] = useState<EnrolementTotp | null>(null);
  const verificationEnCours = useRef(false);
  const enrolementDemarre = useRef(false);

  useEffect(() => {
    if (mode !== 'enrolement' || enrolementDemarre.current) return;
    enrolementDemarre.current = true;
    void demarrerEnrolement();
  }, [mode]);

  async function demarrerEnrolement() {
    setErreur(null);
    try {
      setEnrolement(await preparerEnrolementTotp());
      console.log('[SecondFacteur] Enrôlement TOTP préparé');
    } catch (err) {
      console.error('[SecondFacteur] Enrôlement impossible:', err);
      setErreur('Impossible de préparer la double authentification. Rechargez la page ou contactez un administrateur.');
    }
  }

  async function valider(e: React.FormEvent) {
    e.preventDefault();
    if (verificationEnCours.current) return;
    const idFacteur = mode === 'enrolement' ? enrolement?.id : facteurId;
    if (!idFacteur) return;
    verificationEnCours.current = true;
    setEnCours(true);
    setErreur(null);
    try {
      const resultat = await verifierCodeTotp(idFacteur, code, true);
      if (resultat === 'indisponible') {
        setErreur('Vérification impossible pour le moment. Réessayez.');
        return;
      }
      if (resultat === 'incorrect') {
        console.warn('[SecondFacteur] Code refusé');
        setErreur('Code incorrect. Vérifiez l’heure de votre téléphone et saisissez le code affiché.');
        return;
      }
      console.log('[SecondFacteur] Code accepté : session en aal2');
      onValide();
    } catch (err) {
      console.error('[SecondFacteur] Erreur de vérification:', err);
      setErreur('Erreur de vérification. Réessayez.');
    } finally {
      verificationEnCours.current = false;
      setEnCours(false);
    }
  }

  const titre = mode === 'enrolement' ? 'Configurer la double authentification' : 'Double authentification';

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f1f5f9', padding: '20px' }}>
      <div style={{
        maxWidth: isPhone ? 'calc(100vw - 24px)' : '460px', width: '100%', backgroundColor: 'white',
        borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', padding: isPhone ? '24px' : '36px'
      }}>
        <div style={{ width: '56px', height: '56px', backgroundColor: '#dbeafe', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1 style={{ fontSize: '22px', fontWeight: 600, color: '#1e293b', textAlign: 'center', margin: '0 0 8px' }}>{titre}</h1>
        <p style={{ color: '#64748b', textAlign: 'center', margin: '0 0 20px', fontSize: '14px', lineHeight: 1.5 }}>
          {mode === 'enrolement'
            ? <>Votre compte ({email}) donne un accès étendu : un second facteur est obligatoire avant d’ouvrir l’application.</>
            : <>Saisissez le code à 6 chiffres affiché par votre application d’authentification.</>}
        </p>

        {mode === 'enrolement' && (
          <div style={{ marginBottom: '20px' }}>
            <ol style={{ color: '#334155', fontSize: '14px', paddingLeft: '20px', margin: '0 0 14px', lineHeight: 1.6 }}>
              {ETAPES_ENROLEMENT.map((etape) => <li key={etape}>{etape}</li>)}
            </ol>
            {enrolement ? (
              <BlocCleTotp enrolement={enrolement} />
            ) : !erreur && (
              <p style={{ color: '#64748b', textAlign: 'center', fontSize: '14px' }}>Préparation…</p>
            )}
          </div>
        )}

        <form onSubmit={valider}>
          <ChampCodeTotp valeur={code} onChange={setCode} autoFocus />
          {erreur && (
            <p role="alert" style={{ color: '#dc2626', fontSize: '14px', margin: '10px 0 0' }}>{erreur}</p>
          )}
          <button
            type="submit"
            data-valider-totp
            disabled={enCours || code.length !== 6 || (mode === 'enrolement' && !enrolement)}
            style={{
              width: '100%', marginTop: '16px', padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none',
              borderRadius: '8px', fontSize: '16px', fontWeight: 500, cursor: 'pointer',
              opacity: enCours || code.length !== 6 ? 0.6 : 1
            }}
          >
            {enCours ? 'Vérification…' : mode === 'enrolement' ? 'Activer et continuer' : 'Valider'}
          </button>
        </form>

        <button type="button" onClick={onLogout} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: '#64748b', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}>
          Se déconnecter
        </button>
      </div>
    </div>
  );
}

export default SecondFacteurPage;
